import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form } from "formik";
import { AGCDynamicSelect } from "../../../generic-components/AGCDynamicSelect";
import { AGCStaticSelect } from "../../../generic-components/AGCStaticSelect";
import { ReportColumn } from "../types";

interface ColumnSelectorModalProps {
  show: boolean;
  onHide: () => void;
  columns: ReportColumn[];
  visibleColumns: string[];
  defaultColumns?: string[];
  branchFilter: any;
  dealerFilter: any;
  categoryFilter: any;
  statusFilter: any;
  segmentFilter: any;
  jobTypeFilter: any;
  advisorFilter?: any;
  supervisorFilter?: any;
  onApply: (
    visibleColumns: string[],
    branchId: string | null,
    dealerId: string | null,
    category: string | null,
    status: string | null,
    segment: string | null,
    jobType: string | null,
    advisorId?: string | null,
    supervisorId?: string | null
  ) => void;
}

type FilterMode = "none" | "branch" | "dealer";

const CATEGORY_OPTIONS = [
  { id: "LVT", name: "LVT" },
  { id: "HVT", name: "HVT" },
];

const STATUS_OPTIONS = [
  { id: "TODO", name: "To Do" },
  { id: "IN_PROGRESS", name: "In Progress" },
  { id: "COMPLETE", name: "Complete" },
];

const SEGMENT_OPTIONS = [
  { id: "SMALL", name: "Small" },
  { id: "MEDIUM", name: "Medium" },
  { id: "LARGE", name: "Large" },
  { id: "PREMIUM", name: "Premium" },
];

const JOB_TYPE_OPTIONS = [
  { id: "FREE_SERVICE", name: "Free Service" },
  { id: "PAID_SERVICE", name: "Paid Service" },
  { id: "RUNNING_REPAIR", name: "Running Repair" },
  { id: "ACCIDENTAL_REPAIR", name: "Accidental Repair" },
];

const ColumnSelectorModal: React.FC<ColumnSelectorModalProps> = ({
  show,
  onHide,
  columns,
  visibleColumns,
  defaultColumns: defaultColumnsProp,
  branchFilter,
  dealerFilter,
  categoryFilter,
  statusFilter,
  segmentFilter,
  jobTypeFilter,
  advisorFilter,
  supervisorFilter,
  onApply,
}) => {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(visibleColumns);
  const [filterMode, setFilterMode] = useState<FilterMode>("none");

  // Default visible columns - use prop if provided, otherwise fallback
  const DEFAULT_COLUMNS = defaultColumnsProp || [
    "date", "vehicleNo", "treatmentName", "treatmentCategory",
    "customerPrice", "advisorName", "branchName",
  ];

  useEffect(() => {
    if (show) {
      setSelectedColumns(visibleColumns);
      if (branchFilter) {
        setFilterMode("branch");
      } else if (dealerFilter) {
        setFilterMode("dealer");
      } else {
        setFilterMode("none");
      }
    }
  }, [show, visibleColumns, branchFilter, dealerFilter]);

  const handleColumnToggle = (field: string) => {
    setSelectedColumns((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  const handleSelectAll = () => {
    setSelectedColumns(columns.map((c) => c.field));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Formik
        initialValues={{
          branchId: branchFilter || null,
          dealerId: dealerFilter || null,
          category: categoryFilter || null,
          treatmentStatus: statusFilter || null,
          vehicleSegment: segmentFilter || null,
          jobType: jobTypeFilter || null,
          advisorId: advisorFilter || null,
          supervisorId: supervisorFilter || null,
        }}
        enableReinitialize
        onSubmit={(values) => {
          const category = values.category ? (values.category as any).id || null : null;
          const status = values.treatmentStatus ? (values.treatmentStatus as any).id || null : null;
          const segment = values.vehicleSegment ? (values.vehicleSegment as any).id || null : null;
          const jobType = values.jobType ? (values.jobType as any).id || null : null;
          onApply(selectedColumns, values.branchId || null, values.dealerId || null, category, status, segment, jobType, values.advisorId || null, values.supervisorId || null);
          onHide();
        }}
      >
        {(formik) => (
          <Form>
            <Modal.Header closeButton>
              <Modal.Title className="fw-bold">Select Columns & Filters</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {/* Filter by Branch or Dealer — radio toggle */}
              <h5 className="fw-bold mb-3">Filter</h5>
              <div className="d-flex gap-4 mb-4">
                <label className="form-check form-check-custom form-check-sm">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="filterMode"
                    checked={filterMode === "none"}
                    onChange={() => {
                      setFilterMode("none");
                      formik.setFieldValue("branchId", null);
                      formik.setFieldValue("dealerId", null);
                    }}
                  />
                  <span className="form-check-label fw-semibold">No Filter</span>
                </label>
                <label className="form-check form-check-custom form-check-sm">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="filterMode"
                    checked={filterMode === "branch"}
                    onChange={() => {
                      setFilterMode("branch");
                      formik.setFieldValue("dealerId", null);
                    }}
                  />
                  <span className="form-check-label fw-semibold">Branch</span>
                </label>
                <label className="form-check form-check-custom form-check-sm">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="filterMode"
                    checked={filterMode === "dealer"}
                    onChange={() => {
                      setFilterMode("dealer");
                      formik.setFieldValue("branchId", null);
                    }}
                  />
                  <span className="form-check-label fw-semibold">Dealer</span>
                </label>
              </div>

              {filterMode === "branch" && (
                <div className="mb-4">
                  <AGCDynamicSelect
                    entityId={8}
                    formik={formik}
                    isMulti={false}
                    name="Select Branch"
                    toolTip="Filter report data by a specific branch"
                    value={formik.values.branchId}
                    id="branchId"
                    required={false}
                    searchField="name"
                  />
                </div>
              )}

              {filterMode === "dealer" && (
                <div className="mb-4">
                  <AGCDynamicSelect
                    entityId={7}
                    formik={formik}
                    isMulti={false}
                    name="Select Dealer"
                    toolTip="Filter report data by a specific dealer"
                    value={formik.values.dealerId}
                    id="dealerId"
                    required={false}
                    searchField="name"
                  />
                </div>
              )}

              <div className="row mt-3">
                <div className="col-md-6 mb-3">
                  <AGCStaticSelect
                    data={CATEGORY_OPTIONS}
                    formik={formik}
                    isMulti={false}
                    name="Category"
                    toolTip="Filter by treatment category"
                    value={formik.values.category}
                    id="category"
                    required={false}
                    includeAllOption={true}
                    onChange={undefined}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <AGCStaticSelect
                    data={STATUS_OPTIONS}
                    formik={formik}
                    isMulti={false}
                    name="Status"
                    toolTip="Filter by treatment status"
                    value={formik.values.treatmentStatus}
                    id="treatmentStatus"
                    required={false}
                    includeAllOption={true}
                    onChange={undefined}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <AGCStaticSelect
                    data={SEGMENT_OPTIONS}
                    formik={formik}
                    isMulti={false}
                    name="Segment"
                    toolTip="Filter by vehicle segment"
                    value={formik.values.vehicleSegment}
                    id="vehicleSegment"
                    required={false}
                    includeAllOption={true}
                    onChange={undefined}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <AGCStaticSelect
                    data={JOB_TYPE_OPTIONS}
                    formik={formik}
                    isMulti={false}
                    name="Job Type"
                    toolTip="Filter by job type"
                    value={formik.values.jobType}
                    id="jobType"
                    required={false}
                    includeAllOption={true}
                    onChange={undefined}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <AGCDynamicSelect
                    entityId={11}
                    formik={formik}
                    isMulti={false}
                    name="Advisor"
                    toolTip="Filter report data by a specific advisor"
                    value={formik.values.advisorId}
                    id="advisorId"
                    required={false}
                    searchField="name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <AGCDynamicSelect
                    entityId={2}
                    formik={formik}
                    isMulti={false}
                    name="Supervisor"
                    toolTip="Filter report data by a specific supervisor"
                    value={formik.values.supervisorId}
                    id="supervisorId"
                    required={false}
                    searchField="name"
                  />
                </div>
              </div>

              <div className="separator my-4"></div>

              {/* Column selection */}
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <h5 className="fw-bold mb-0">Columns</h5>
                  <span className="text-muted fs-8">Selected columns will be available in Print and CSV export</span>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light-danger"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              <div className="row">
                {columns.map((col) => (
                  <div key={col.field} className="col-md-6 col-lg-4 mb-3">
                    <label className="form-check form-check-custom form-check-sm">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedColumns.includes(col.field)}
                        onChange={() => handleColumnToggle(col.field)}
                      />
                      <span className="form-check-label fw-semibold">
                        {col.displayName}
                      </span>
                    </label>
                  </div>
                ))}
              </div>

              {selectedColumns.length === 0 && (
                <div className="alert alert-warning mt-3 mb-0 py-2">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Please select at least one column.
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  setSelectedColumns(DEFAULT_COLUMNS);
                  setFilterMode("none");
                  formik.setFieldValue("branchId", null);
                  formik.setFieldValue("dealerId", null);
                  formik.setFieldValue("category", null);
                  formik.setFieldValue("treatmentStatus", null);
                  formik.setFieldValue("vehicleSegment", null);
                  formik.setFieldValue("jobType", null);
                  formik.setFieldValue("advisorId", null);
                  formik.setFieldValue("supervisorId", null);
                }}
              >
                Reset to Default
              </button>
              <button
                type="button"
                className="btn btn-light-secondary"
                onClick={onHide}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={selectedColumns.length === 0}
              >
                Apply
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ColumnSelectorModal;
